import { Alert, Button, Checkbox, Grid, Link, List, ListItem, Stack, Table, Typography } from '@mui/joy';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { isProcessOpen } from '@/components/processes/utils/isProcessOpen';
import { SignCodeIcon } from '@/components/signs/SignCodeIcon';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { useAppAbility } from '@/hooks/useAppAbility';
import { I18N_DE } from '@/i18n/de';
import { backendApiSignsFindOne } from '@/modules/api';
import { backendApiProcessesFindAllOptions } from '@/modules/api/@tanstack/react-query.gen';
import { useSignGroupQuery } from '@/queries/useSignGroupQuery';
import { useProcessMapStore } from '@/stores/useProcessMapStore';

type ProcessModalProps = { setOpen: (open: boolean) => void };

export function ProcessModal({ setOpen }: ProcessModalProps) {
  const { adminLevelId, signSupportId, signGroupId } = useParams({ strict: false });
  const { data: group } = useSignGroupQuery();
  const [selectedSignIds, setSelectedSignIds] = useState<string[]>([]);
  const { ability } = useAppAbility();
  const { addSigns } = useProcessMapStore((state) => state.actions);
  const navigate = useNavigate();
  const {
    mutate: fetchSelectedSigns,
    isPending: isFetchingSelectedSigns,
    error: fetchSelectedSignsError,
    reset: resetFetchSelectedSigns,
  } = useMutation({
    mutationFn: async (signIds: string[]) =>
      Promise.all(
        signIds.map(async (signId) => {
          const { data } = await backendApiSignsFindOne({ path: { signId } });
          if (!data) {
            throw new Error('Das Verkehrszeichen konnte nicht geladen werden.');
          }

          return data;
        }),
      ),
    onSuccess: (signs) => {
      addSigns(signs);
      navigate({ to: `/vorgaenge/${adminLevelId}/karte` });
    },
  });

  const { data: processes } = useQuery(
    backendApiProcessesFindAllOptions({ query: { signIds: group?.signs?.map((sign) => sign.id) } }),
  );

  if (!adminLevelId || !signSupportId || !signGroupId || !group || !processes) {
    return null;
  }

  return (
    <EditModalDialog onClose={() => setOpen(false)} title="Vorgang erstellen / anzeigen">
      <Stack gap={1}>
        {processes.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>Vorgangs-Nr.</th>
                <th>Status</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {processes.map((process) => (
                <tr key={process.id}>
                  <td>{process.sequentialId}</td>
                  <td>{I18N_DE.ProcessStatus[process.status]}</td>
                  <td>
                    <Stack direction="row" divider={<Typography>|</Typography>} gap={1}>
                      <Link
                        key={process.id}
                        component={RouterLink}
                        to={`/vorgaenge/${adminLevelId}/${process.id}`}
                        onClick={() => setOpen(false)}
                      >
                        anzeigen
                      </Link>
                      {isProcessOpen(process.status) && (
                        <Link component={RouterLink} to={`/vorgaenge/${adminLevelId}/${process.id}/edit`} fontSize={14}>
                          bearbeiten
                        </Link>
                      )}
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <List>
          {group.signs?.map((sign) => {
            const openProcess = processes.find(
              (process) => process.signsDraft.find((s) => s.id === sign.id) && isProcessOpen(process.status),
            );

            return (
              <ListItem key={sign.id} component={Grid} container>
                <Grid xs={6}>
                  <Stack direction="row">
                    <SignCodeIcon code={sign.code} style={{ width: 30, height: 30, marginRight: 10 }} />
                    <Typography sx={{ whiteSpace: 'nowrap' }}>{`Verkehrszeichen ${sign.code}`}</Typography>
                  </Stack>
                </Grid>
                <Grid xs={5}>
                  <Stack direction="row">
                    {openProcess ? (
                      <Alert color="danger" variant="plain">
                        Es ist nur ein offener Vorgang pro Verkehrszeichen erlaubt.
                      </Alert>
                    ) : (
                      <Checkbox
                        disabled={!!openProcess}
                        overlay
                        sx={{ marginLeft: 1 }}
                        checked={selectedSignIds.includes(sign.id)}
                        onChange={() =>
                          setSelectedSignIds(
                            selectedSignIds.includes(sign.id)
                              ? selectedSignIds.filter((id) => id !== sign.id)
                              : [...selectedSignIds, sign.id],
                          )
                        }
                      />
                    )}
                  </Stack>
                </Grid>
              </ListItem>
            );
          })}
        </List>

        {ability.can('CREATE', 'Process') && (
          <Button loading={isFetchingSelectedSigns} onClick={() => fetchSelectedSigns(selectedSignIds)}>
            Vorgang erstellen
          </Button>
        )}

        <ErrorAlert error={fetchSelectedSignsError} onClose={resetFetchSelectedSigns} />
      </Stack>
    </EditModalDialog>
  );
}
